import type { CompletionCriteria, PlayerConfig } from '../course/model.js';
import type { FidelityReport } from '../fidelity.js';
import type { Quiz } from '../quiz/model.js';
import type { RenderedSlide } from '../render/model.js';
import type { Interaction } from '../interaction/model.js';

/**
 * `course.json` — the contract between the publisher and the player.
 *
 * One runtime, forever (AGENTS.md): a SCORM zip, a plain HTML5 folder and (in
 * Part II) the Learn platform all hand the player exactly this document. Delivery
 * differences live in `capabilities`, never in a second copy of the player.
 */
export const COURSE_FORMAT_VERSION = 3;

export interface PublishedMedia {
  readonly id: string;
  /** Package-relative path, e.g. `media/3f2a....png`. */
  readonly href: string;
  readonly mimeType: string;
  readonly byteLength: number;
}

export interface PublishedSlide {
  readonly id: string;
  readonly index: number;
  readonly title: string;
  readonly kind: 'content' | 'quiz' | 'interaction';
  /** Plain-text notes; the caption/TTS source and the transcript panel content. */
  readonly notes: string;
  readonly render: RenderedSlide;
  readonly quiz?: Quiz;
  /** Structured M8 content authored for this slide. */
  readonly interaction?: Interaction;
  /** Present when the slide has an active narration take. */
  readonly narration?: PublishedNarration;
  readonly transitionMs: number;
  readonly advanceAfterMs?: number;
}

/**
 * Narration as the player sees it: one file, already chosen.
 *
 * Takes, devices and sync markers are authoring concerns and stay in
 * `CourseData`. The player only needs to know what to play and how long it is.
 */
export interface PublishedNarration {
  readonly href: string;
  readonly mimeType: string;
  readonly durationMs: number;
  readonly captions?: readonly { readonly startMs: number; readonly endMs: number; readonly text: string }[];
  /** Package-relative WebVTT file used by the native audio control. */
  readonly captionsHref?: string;
}

export interface PlayerCapabilities {
  /** The player writes progress through an LMS adapter rather than only locally. */
  readonly tracking: 'none' | 'scorm12' | 'scorm2004' | 'xapi' | 'cmi5';
  /** file:// delivery inlines course.json instead of fetching it. */
  readonly inlineCourse: boolean;
  readonly allowPrint: boolean;
}

export interface PublishedCourse {
  readonly formatVersion: typeof COURSE_FORMAT_VERSION;
  readonly courseId: string;
  readonly title: string;
  readonly description: string;
  readonly language: string;
  readonly stageWidth: number;
  readonly stageHeight: number;
  readonly player: PlayerConfig;
  readonly completion: CompletionCriteria;
  readonly capabilities: PlayerCapabilities;
  readonly slides: readonly PublishedSlide[];
  readonly media: readonly PublishedMedia[];
  readonly fidelity: FidelityReport;
  /** True only when at least one active take is synthetic and disclosure is enabled. */
  readonly syntheticNarration: boolean;
  readonly generator: { readonly name: string; readonly version: string };
  /** Set once per publish; the only field allowed to differ between two builds. */
  readonly publishedAt: string;
}

export function quizSlidesOf(course: PublishedCourse): readonly PublishedSlide[] {
  return course.slides.filter((s) => s.quiz !== undefined);
}

export function totalQuizPoints(course: PublishedCourse): number {
  return quizSlidesOf(course).reduce(
    (sum, s) => sum + (s.quiz?.questions.reduce((a, q) => a + q.points, 0) ?? 0),
    0,
  );
}
